import { io } from "socket.io-client";
import { API_URL } from "../../../utils/constants";

export function connectCrmSocket({ workspaceId, onChange, onConnect, onDisconnect, onDenied, onUnavailable }) {
  const base = String(API_URL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
  let retryTimer, closed = false, denied = false, retryDelay = 1000;
  const socket = io(base + "/crm", {
    forceNew: true, transports: ["websocket", "polling"], tryAllTransports: true,
    reconnection: true, reconnectionDelay: 1000, reconnectionDelayMax: 15000,
    randomizationFactor: 0.5,
    auth: done => done({ token: localStorage.getItem("token"), workspaceId }),
  });
  const retry = () => {
    clearTimeout(retryTimer);
    if (closed || denied || navigator.onLine === false) return;
    retryTimer = setTimeout(() => { if (!closed && !denied) socket.connect(); }, retryDelay);
    retryDelay = Math.min(retryDelay * 2, 30000);
  };
  const deny = () => {
    denied = true; clearTimeout(retryTimer); onDenied(); socket.disconnect();
  };
  const reconnect = () => {
    if (closed || denied || socket.connected) return;
    clearTimeout(retryTimer); socket.connect();
  };
  socket.on("crm:changed", payload => {
    if (payload?.workspaceId === workspaceId && Number.isInteger(payload.revision)) onChange(payload.revision);
  });
  socket.on("crm:ready", () => { clearTimeout(retryTimer); retryDelay = 1000; onConnect(); });
  socket.on("disconnect", reason => {
    if (closed || denied) return;
    onDisconnect();
    // Socket.IO does not reconnect automatically after a server disconnect.
    if (reason === "io server disconnect") retry();
  });
  socket.on("connect_error", error => {
    if (error.data?.code === "CRM_ACCESS_DENIED") deny();
    else {
      if (error.message === "Invalid namespace") onUnavailable?.(); else onDisconnect();
      // Namespace errors do not trigger the Manager's transport reconnection.
      retry();
    }
  });
  socket.on("crm:access-revoked", deny);
  window.addEventListener("online", reconnect);
  return () => {
    closed = true; clearTimeout(retryTimer); window.removeEventListener("online", reconnect);
    socket.removeAllListeners(); socket.disconnect();
  };
}

// One request at a time, bounded batching and retry. A newer revision arriving
// during a request remains pending until a subsequent successful read.
export function createCrmRefreshController({ refresh, getRevision, canRefresh, delay = 200 }) {
  let wanted = -1, forced = false, running = false, disposed = false, timer = null, retry = 1000;
  const needed = () => forced || wanted > (getRevision() ?? -1);
  const schedule = ms => {
    if (!disposed && !timer && !running && needed()) timer = setTimeout(run, ms);
  };
  async function run() {
    timer = null;
    if (disposed || !needed()) return;
    if (!canRefresh()) { schedule(1000); return; }
    running = true;
    const forceAtStart = forced; forced = false;
    try {
      const result = await refresh();
      if (!result?.ok) { forced ||= forceAtStart; retry = Math.min(retry * 2, 30000); }
      else retry = 1000;
    } catch { forced ||= forceAtStart; retry = Math.min(retry * 2, 30000); }
    finally { running = false; schedule(retry === 1000 ? delay : retry); }
  }
  return {
    notify(revision) { wanted = Math.max(wanted, revision); schedule(delay); },
    wake() { forced = true; schedule(delay); },
    resume() { schedule(delay); },
    dispose() { disposed = true; clearTimeout(timer); },
  };
}

export function shareCrmState(previous, next) {
  const result = { ...next };
  for (const key of Object.keys(next)) {
    const old = previous[key], value = next[key];
    if (old === value) continue;
    if (Array.isArray(old) && Array.isArray(value)) {
      const byId = new Map(old.map(row => [row.id, row]));
      const rows = value.map(row => {
        const prior = byId.get(row.id);
        return prior && JSON.stringify(prior) === JSON.stringify(row) ? prior : row;
      });
      result[key] = rows.length === old.length && rows.every((row, i) => row === old[i]) ? old : rows;
    } else if (JSON.stringify(old) === JSON.stringify(value)) result[key] = old;
  }
  return Object.keys(result).every(key => result[key] === previous[key]) ? previous : result;
}
